import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ViewerComponent } from "../../components/viewer/ViewerComponent";
import { ViewerProvider } from "../../contexts/ViewerContext/ViewerProvider";
import { ViewCubeComponent } from "../../components/viewer/ViewCubeComponent";
import { InfoBox } from "../../components/InfoBox";
import { useLoadingPlan } from "../../contexts/LoadingPlanContext";
import { DataTable, Column } from "../../components/ui/DataTable";
import "./ViewLoadingPlanPage.scss";
import { loadingPlansService, partsService } from "../../services";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../consts/routes";
import { Part } from "../../services/api/partsService";

interface PartRow {
  id: number;
  partId: string;
  mark: string;
  name?: string;
  type: string;
  subType: string;
  weight: number;
  layer: string;
  layerNumber: number;
}

const ViewLoadingPlanPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [allParts, setAllParts] = useState<PartRow[]>([]);
  const [currentTabParts, setCurrentTabParts] = useState<PartRow[]>([]);
  const [planInfo, setPlanInfo] = useState({ trailerLength: 0, trailerMaxWeight: 0, totalWeight: 0, layersCount: 0 });

  const { currentLoadingPlan, setCurrentLoadingPlan, updateCurrentLoadingPlan, clearCurrentLoadingPlan } = useLoadingPlan();

  const { loadingPlanId } = useParams();
  const navigate = useNavigate();

  // Define table columns
  const baseColumns: Column<PartRow>[] = [
    { id: 'id', label: '#', className: 'table-cell-number', width: 60 },
    { id: 'name', label: 'Name', className: 'table-cell-name', minWidth: 100 },
    { id: 'mark', label: 'Mark', className: 'table-cell-mark', minWidth: 100 },
    { id: 'sequenceNumber', label: 'Sequence', className: 'table-cell-sequence', minWidth: 100 },
    { id: 'type', label: 'Type', className: 'table-cell-type', minWidth: 120 },
    { id: 'subType', label: 'Sub Type', className: 'table-cell-sub-type', minWidth: 120 },
    { id: 'weight', label: 'Weight, lbs', className: 'table-cell-weight', minWidth: 120, align: 'right' },
    { id: 'layer', label: 'Layer', className: 'table-cell-layer', minWidth: 100 },
  ];

  const columns = baseColumns;

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBackToDashboard = () => {
    clearCurrentLoadingPlan();
    navigate(ROUTES.home);
  };

  const handleExportPdf = async () => {
    if (!currentLoadingPlan.id) return;
    try {
      await partsService.exportPdf(currentLoadingPlan.id);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    }
  };

  // Consolidated initialization effect - fetch data and calculate all info on load
  useEffect(() => {
    const fetchData = async () => {
      if (!currentLoadingPlan.id && loadingPlanId) {
        const loadingPlan = await loadingPlansService.fetchLoadingPlanById(+loadingPlanId);
        setCurrentLoadingPlan(loadingPlan);

        const includedParts = await partsService.fetchLoadingPlanViewParts(loadingPlan.id);
        updateCurrentLoadingPlan({ includedParts: includedParts.parts });
      }
    };

    fetchData();
  }, [currentLoadingPlan, loadingPlanId, setCurrentLoadingPlan, updateCurrentLoadingPlan]);

  // Calculate all plan info and parts data when nesting output and included parts are available
  useEffect(() => {
    if (currentLoadingPlan.nestingOutput?.nested_parts && currentLoadingPlan.includedParts) {
      // Create parts mapping
      const partsMap = new Map(
        (currentLoadingPlan.includedParts || []).map((part: Part) => [`${part.sanitizedName}_${part.mark}`, part])
      );

      // Get trailer info
      const trailerLength = currentLoadingPlan.trailerLength || 0;
      const trailerMaxWeight = currentLoadingPlan.trailerMaxWeight || 0;

      // Calculate total weight
      const totalWeight = (currentLoadingPlan.includedParts || []).reduce(
        (sum: number, part: any) => sum + (part.weight || 0),
        0
      );

      // Calculate layers count from nesting output
      const layersCount = currentLoadingPlan.nestingOutput?.nested_parts
        ? Math.max(...currentLoadingPlan.nestingOutput.nested_parts.map((part: any) => part.assigned_loading_layer))
        : 0;

      // Filter and map parts (excluding support objects)
      const allPlanRows: PartRow[] = currentLoadingPlan.nestingOutput.nested_parts
        .filter((part: any) => {
          const basePartId = part.part_id.includes('#') ? part.part_id.split('#')[0] : part.part_id;
          return basePartId !== 'dunnage';
        })
        .flatMap((nestingPart: any, index: number) => {
          const basePartId = nestingPart.part_id.includes('#') ? nestingPart.part_id.split('#')[0] : nestingPart.part_id;
          const isPallet = basePartId === 'pallet';

          const items: any[] = isPallet ? (nestingPart.child_items || []) : [nestingPart];

          return items.map((item: any) => {
            const partId = item.part_id;
            const databasePart = currentLoadingPlan.includedParts?.find((p: Part) => partId === `${p.sanitizedName}_${p.globalId}`);
            const partInfo = databasePart || partsMap.get(partId);

            return {
              id: 0,
              partId,
              name: partInfo?.name,
              mark: partInfo?.mark || nestingPart.part_id,
              sequenceNumber: partInfo?.sequenceNumber || 0,
              type: partInfo?.type || '-',
              subType: partInfo?.subType || '-',
              weight: parseFloat((partInfo?.weight || 0).toFixed(2)),
              layer: `Layer ${nestingPart.assigned_loading_layer}`,
              layerNumber: nestingPart.assigned_loading_layer,
            };
          });
        })
        .map((row: PartRow, index: number) => ({ ...row, id: index + 1 }));

      // Set all state
      setPlanInfo({
        trailerLength,
        trailerMaxWeight,
        totalWeight,
        layersCount,
      });
      setAllParts(allPlanRows);
      setCurrentTabParts(allPlanRows);
    }
  }, [currentLoadingPlan.nestingOutput, currentLoadingPlan.includedParts, currentLoadingPlan.trailerLength, currentLoadingPlan.trailerMaxWeight]);

  // Handle layer tab changes
  useEffect(() => {
    if (allParts.length > 0) {
      setCurrentTabParts(allParts.filter((row) => activeTab === 0 || row.layer === `Layer ${activeTab}`));
    }
  }, [activeTab, allParts]);

  return (
    <ViewerProvider>
      <div className="view-loading-plan-page">
        <Box className="back-button-row" onClick={handleBackToDashboard} style={{ cursor: 'pointer' }}>
          <IconButton size="small" className="back-icon-button">
            <ArrowBackIcon color="primary" />
          </IconButton>
          <Typography variant="body2" color="primary" fontWeight={500}>
            BACK TO DASHBOARD
          </Typography>
        </Box>
        <Paper className="form-card">
          <Box className="table-container">
            {/* Page Title */}
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              {currentLoadingPlan.title}
            </Typography>

            {/* Stats */}
            <Box className="stats-row">
              <InfoBox label="Parts" value={allParts.length || 0} />
              <InfoBox label="Trailer" value={planInfo.trailerLength + " ft"} />
              <InfoBox label="Max Weight Capacity" value={planInfo.trailerMaxWeight + " lbs"} />
              <InfoBox label="Total Weight" value={planInfo.totalWeight.toFixed(2) + " lbs"} />
            </Box>
          </Box>

          {/* Viewer Preview */}
          <Box mb={2}>
            <Box className="viewer-container">
              <ViewerComponent showNavigation={true} />
              <Box className="view-cube-wrapper">
                <ViewCubeComponent />
              </Box>
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={handleTabChange} className="tabs-container">
            <Tab label="ALL" />
            {Array.from({ length: planInfo.layersCount }, (_, i) => (
              <Tab key={i + 1} label={`LAYER ${i + 1}`} />
            ))}
          </Tabs>

          {/* Table */}
          <Box className="table-container" sx={{ marginBottom: "0px !important" }}>
            <DataTable
              columns={columns}
              data={currentTabParts}
              size="small"
              maxHeight={400}
              emptyMessage="No parts included in this loading plan."
              className="table-content"
              headerClassName="table-head"
            />
          </Box>
        </Paper>
        <Paper className="form-card export-pdf-container">
          <Button variant="outlined" color="primary" onClick={handleExportPdf}>
            EXPORT PDF
          </Button>
        </Paper>
      </div>
    </ViewerProvider>
  );
};

export { ViewLoadingPlanPage };
